import { useTu } from "@src/appV2/i18n/useTu";
import type { TuFunction } from "@src/appV2/i18n/types";
import { Box, type Theme } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { formatRushFeeMessage, type RushFeeInfo } from "@src/appV2/redesign/utils/rushFee";
import { type UseModalState } from "@src/lib/ui-react";
import { InfoBanner } from "../../../components/InfoBanner/InfoBanner";
import { type InviteWarningReason } from "./types";

function getWarningContent(
  warning: InviteWarningReason,
  tu: TuFunction
): { title: string; message: string } {
  switch (warning.type) {
    case "SHIFT_OVERWORKING": {
      return {
        title: tu("Potential overwork"),
        message: tu("This shift may lead to work exceeding the recommended 17-hour limit."),
      };
    }
    case "SHIFT_OVERWORKING_FROM_INVITES": {
      return {
        title: tu("Potential overwork from invites"),
        message: tu(
          "If all pending invites are accepted, this shift may lead to work exceeding the recommended 17-hour limit."
        ),
      };
    }
    case "SHIFT_OVERLAPPING": {
      return {
        title: tu("Overlapping shift"),
        message: tu("This worker already has a booked shift that overlaps with this time slot."),
      };
    }
    case "HIGH_CALLOFF_RATE": {
      return {
        title: tu("High call-off rate"),
        message: tu("This professional has a call-off rate of {{calloffRate}}%.", {
          values: { calloffRate: warning.details.calloffRate },
        }),
      };
    }
    case "MISSING_BACKGROUND_CHECK_DATA": {
      return {
        title: tu("Missing background check data"),
        message: tu("This professional is missing background check data."),
      };
    }
    case "MISSING_FACILITY_REQUIRED_DOCS": {
      const documentList = warning.details.missingDocuments.join(", ");
      return {
        title: tu("Worker is missing workplace requirements"),
        message: tu(
          "Missing documents: {{documentList}}. You can still send the invite, but the worker may be unable to clock in until those documents are uploaded.",
          {
            values: { documentList },
          }
        ),
      };
    }
  }
}

interface InviteWarningModalProps {
  modalState: UseModalState;
  warningReasons: InviteWarningReason[];
  rushFeeInfo?: RushFeeInfo;
  /**
   * When provided, the secondary action clears the selection so the user can pick another professional.
   * When absent (preselected-worker flow), the secondary action just dismisses the modal.
   */
  onGoBack?: () => void;
  onProceed: () => void;
  isLoading?: boolean;
}

/**
 * Modal displayed when invite validation returns overridable warnings.
 * User can choose to proceed anyway or go back and select another professional.
 *
 * Per spec:
 * - "Go back" button returns to professional selection
 * - "Send invite" button proceeds with the invite
 * - Shows rush fee banner if applicable
 */
export function InviteWarningModal(props: InviteWarningModalProps) {
  const { tu } = useTu("schedule");

  const { modalState, warningReasons, rushFeeInfo, onGoBack, onProceed, isLoading = false } = props;

  const handleGoBack = () => {
    modalState.closeModal();
    onGoBack?.();
  };

  return (
    <ResponsiveDialog
      dismissible={false}
      modalState={modalState}
      title={tu("Review invite")}
      footerContent={
        <DialogFooter
          layout="horizontal"
          primaryAction={{
            label: tu("Send invite"),
            onClick: onProceed,
            isLoading,
          }}
          secondaryAction={{
            label: onGoBack ? tu("Go back") : tu("Close"),
            onClick: handleGoBack,
          }}
        />
      }
      onClose={handleGoBack}
    >
      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(4),
        })}
      >
        {warningReasons.map((warning) => {
          const { title, message } = getWarningContent(warning, tu);
          return (
            <InfoBanner key={warning.type} message={message} title={title} variant="warning" />
          );
        })}

        {rushFeeInfo && (
          <InfoBanner
            message={formatRushFeeMessage(rushFeeInfo)}
            title={tu("Rush fee applies")}
            variant="warning"
          />
        )}

        <Text
          sx={(theme: Theme) => ({
            color: theme.palette.text?.secondary,
          })}
          variant="body2"
        >
          {tu("Do you want to proceed with sending this invite?")}
        </Text>
      </Box>
    </ResponsiveDialog>
  );
}
